import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, startWith, map } from 'rxjs';
import { Language } from '../../shared/models/language';
import { CustomPassengerName } from '../../shared/models/ticket';

@Injectable({ providedIn: 'root' })
export class Utility {
    private readonly ZONE_PREFIX_MAP: Record<number, string> = {
        1: 'DOT',
        2: 'FB',
        3: 'ST',
        4: 'MT',
        5: 'NT',
        6: 'BAT',
        7: 'DSB'
    };

    private readonly PASSENGER_TYPE_MAP: Record<string, string> = {
        'adult': 'A',
        'child': 'C',
        'young': 'Y',
        'senior': 'S',
        'handicapped': 'H',
        'bike': 'B',
        'dog': 'D'
    };

    constructor(private translateService: TranslateService) { }

    public formatZoneCode(zoneId: number): string {
        const zoneCode = zoneId.toString().padStart(4, '0');
        const ptoMapping = this.tryConvertToShorthand(zoneId);

        if (!ptoMapping) {
            return zoneCode;
        }

        const zoneNumberPart = zoneCode.substring(1);
        return `${ptoMapping}${zoneNumberPart}`;
    }

    private tryConvertToShorthand(zoneId: number): string | null {
        const firstDigit = Math.floor(zoneId / 1000);
        return this.ZONE_PREFIX_MAP[firstDigit] || null;
    }

    public getLocalizedText(obj: any, locale: string): string {
        return obj?.[locale] || obj?.['en-US'] || obj?.['da-DK'] || '';
    }

    private static getUserAgent(): string {
        return navigator.userAgent || navigator.vendor || (window as any).opera;
    }

    public static isUserIOS(): boolean {
        const userAgent = this.getUserAgent();
        return /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    public static isUserAndroid(): boolean {
        const userAgent = this.getUserAgent();
        return /Android/i.test(userAgent);
    }

    public getLocaleKey(): Observable<string> {
        return this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.getCurrentLang() }),
            map(event => event.lang === Language.Danish ? 'da-DK' : 'en-US')
        );
    }

    public getCustomPassengerLabel(
        passengerType: string,
        customPassengerNames: CustomPassengerName[] | undefined,
        currentLocale: string
    ): string | null {
        if (!customPassengerNames || !Array.isArray(customPassengerNames)) {
            return null;
        }

        const currentLanguage = currentLocale.startsWith('da') ? Language.Danish : Language.English;

        const typeKey = passengerType.replace('web.', '').toLowerCase();
        const backendType = this.PASSENGER_TYPE_MAP[typeKey] || typeKey;

        const customName = customPassengerNames.find(
            (name: any) => name.language === currentLanguage && name.passengerType === backendType
        );

        return customName?.name ?? null;
    }
}
